<template>
  <b-overlay :show="loading">
    <b-alert
      variant="danger"
      :show="patientData === undefined && !loading"
    >
      <h4 class="alert-heading">
        Error fetching Patient details
      </h4>
      <div class="alert-body">
        <b-link
          class="alert-link"
          :to="{ name: 'admin-patients'}"
        >
          Patients
        </b-link>
      </div>
    </b-alert>

    <template v-if="patientData">
      <validation-observer ref="newUserForm" #default="{invalid}">
        <b-form ref="form" @submit.prevent="onUpdateUser">
          <b-row>
            <b-col
              cols="12"
              xl="8"
              md="8"
              >
              <div>
                <b-card>
                  <b-row class="">
                    <b-col cols="11">
                      <div class="d-flex align-items-center">
                        <feather-icon
                          icon="UserIcon"
                          size="19"
                        />
                        <h4 class="mb-0 ml-50">
                          Personal Information
                        </h4>
                      </div>
                    </b-col>
                  </b-row>

                  <!-- Media -->
                  <b-media class="mt-3 mb-3">
                    <template #aside>
                      <b-avatar
                        ref="previewEl"
                        :text="avatarText(`${patientData.full_name}`)"
                        :variant="`light-success`"
                        size="6rem"
                      />
                    </template>
                  </b-media>

                  <b-row>
                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="First Name" rules="required">
                        <b-form-group label-for="first_name">
                          <template #label>
                            <span>First Name<span style="color: tomato">*</span></span>
                          </template>

                          <b-form-input
                            id="first_name"
                            v-model="patientData.first_name"
                            placeholder="First name"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />

                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Last Name" rules="required">
                        <b-form-group label-for="last_name">
                          <template #label>
                            <span>Last Name<span style="color: tomato">*</span></span>
                          </template>

                          <b-form-input
                            id="last_name"
                            v-model="patientData.last_name"
                            placeholder="Last name"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />

                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>

                  <b-row class="mt-2">
                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Gender" rules="required">
                        <b-form-group label-for="gender">
                          <template #label>
                            <span>Gender<span style="color: tomato">*</span></span>
                          </template>

                          <b-form-select
                            id="gender"
                            v-model="patientData.gender"
                            placeholder="Gender"
                            :disabled="disableForm"
                            :options="genderOptions"
                            :state="getValidationState(validationContext)"
                          />

                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Phone Number" rules="required|length:10">
                        <b-form-group label-for="phone">
                          <template #label>
                            <span>Phone Number<span style="color: tomato">*</span></span>
                          </template>

                          <b-form-input
                            id="phone"
                            v-model="patientData.phone"
                            placeholder="Phone number"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />

                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>

                  <b-row class="mt-2">
                    <b-col cols="12" md="6">
                      <validation-provider #default name="Date of Birth" rules="required">
                        <b-form-group label="Date of Birth" label-for="date_of_birth">
                          <template #label>
                            <span>Date of Birth<span style="color: tomato">*</span></span>
                          </template>
        
                          <validation-provider
                            #default="{ errors }"
                            name="date_of_birth"
                            rules="required|length:10"
                          >
                            <b-input-group>
                              <cleave 
                                v-model="patientData.date_of_birth"
                                :options="cleaveDateOptions"
                                class="form-control"
                                name="date_of_birth"
                                placeholder="YYYY-MM-DD"
                                :raw="false"
                              />
                            </b-input-group>
                            <small class="text-danger">{{ errors[0] }}</small>
                          </validation-provider>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Email Address" rules="email">
                        <b-form-group label-for="email">
                          <template #label>
                            <span>Email Address</span>
                          </template>

                          <b-form-input
                            id="email"
                            v-model="patientData.email"
                            placeholder="Email address"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />

                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>

                  <b-row class="mt-2">
                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Insurance Name" rules="">
                        <b-form-group label-for="insurance_name">
                          <template #label>
                            <span>Insurance Name</span>
                          </template>
          
                          <b-form-input
                            id="insurance_name"
                            v-model="patientData.insurance_name"
                            placeholder="Insurance name"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />
          
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
          
                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Policy Number" rules="">
                        <b-form-group label-for="policy_number">
                          <template #label>
                            <span>Policy Number</span>
                          </template>
          
                          <b-form-input
                            id="policy_number"
                            v-model="patientData.policy_number"
                            placeholder="Policy number"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />
          
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>

                  <b-row class="mt-2">
                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Region" rules="required">
                        <b-form-group label-for="region">
                          <template #label>
                            <span>Region</span>
                          </template>
          
                          <b-form-select
                            id="region"
                            v-model="patientData.region"
                            placeholder="Region"
                            :disabled="disableForm"
                            :options="regionOptions"
                            :state="getValidationState(validationContext)"
                          />
          
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
          
                    <b-col cols="12" md="6">
                      <validation-provider #default="validationContext" name="Location" rules="required">
                        <b-form-group label-for="location">
                          <template #label>
                            <span>Location</span>
                          </template>
          
                          <b-form-input
                            id="location"
                            v-model="patientData.location"
                            placeholder="Location"
                            :disabled="disableForm"
                            :state="getValidationState(validationContext)"
                          />
          
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>

                 <b-button
                  class="mb-5"
                  style="width: 200px"
                  variant="primary"
                  block
                  type="submit"
                  :disabled="invalid || disableForm || loading"
                >
                  Update {{ capitalizeFirstWord(getValueFromSource(patientData, 'user_type')) }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </template>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BAlert, BLink, BRow, BCol,
  BCard, BCardBody, BButton, BFormGroup, 
  BForm, BFormInput, BMedia, BAvatar,
  BFormFile, BFormDatepicker, BBadge,
  BInputGroup, BInputGroupPrepend, BDropdown,
  BDropdownItem, BFormSelect, BFormInvalidFeedback
} from "bootstrap-vue"

import { get } from "lodash";
import { required, email } from '@validations'

import Cleave from 'vue-cleave-component';
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';


export default {
  components: {
    BOverlay,
    BAlert,
    BLink,
    BRow,
    BCol,
    BCard, 
    BCardBody, 
    BButton,
    BFormGroup, 
    BForm, 
    BFormInput,
    BMedia, 
    BAvatar,
    BFormFile,
    BFormDatepicker,
    BBadge,
    BInputGroup,
    BDropdown,
    BFormSelect,
    BDropdownItem,
    BInputGroupPrepend,
    BFormInvalidFeedback,
    Cleave,
    
    // validations
    ValidationProvider,
    ValidationObserver,
  },
  directives: {},
  data(){
    return {
      loading: false,

      patientData: null,
      permissionOptions: [],

      avatarPath: "",
      updatedImage: null,

      genderOptions: [
        { value: 'male', text: 'Male' },
        { value: 'female', text: 'Female' },
      ],

      regionOptions: [],

      cleaveDateOptions: {
        date: true,
        raw: true,
        delimiter: '-',
        datePattern: ['Y', 'm', 'd'],
        blocks: [4, 2, 2],
      },

      required,
      email
    }
  },
  computed: {
    hasBasicUpdatePermission(){
      return this.can('patients:CanUpdate');
    },
    disableForm(){
      return !this.can('patients:CanUpdate');
    },
  },
  created(){
    this.fetchPatientDetails();
    this.fetchGeoData();
  },
  methods: {
    async fetchPatientDetails(){
      try {
        this.loading = true;
        const { id } = this.$route.params;
        
        const response = await this.useJwt().mainService.patients.fetch(id);
        const patientData = get(response, 'data.data');
        
        this.patientData = patientData
      } catch (error){
        this.patientData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchGeoData(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const query = {
          geo_id: 2300660
        }
        
        const response = await this.useJwt().mainService.geoData.list(query);
        const data = get(response, 'data.data');

        this.regionOptions = data.map((reg) => ({
          value: reg.name,
          text: reg.name,
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onUpdateUser(){
      try {
        this.loading = true;

        const formData = new FormData();

        if (this.hasBasicUpdatePermission){
          formData.append('first_name', this.getValueFromSource(this.patientData, 'first_name', ''));
          formData.append('last_name', this.getValueFromSource(this.patientData, 'last_name', ''));
          formData.append('phone', this.getValueFromSource(this.patientData, 'phone', ''));
          formData.append('email', this.getValueFromSource(this.patientData, 'email', ''));
          formData.append('gender', this.getValueFromSource(this.patientData, 'gender', ''));
          formData.append('date_of_birth', this.getValueFromSource(this.patientData, 'date_of_birth', ''));
          formData.append('insurance_name', this.getValueFromSource(this.patientData, 'insurance_name', ''));
          formData.append('policy_number', this.getValueFromSource(this.patientData, 'policy_number', ''));
          formData.append('region', this.getValueFromSource(this.patientData, 'region', ''));
          formData.append('location', this.getValueFromSource(this.patientData, 'location', ''));
        }

        if (!this.hasBasicUpdatePermission){
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: "An unexpected error occurred. You might not have permission to update record."
            },
          });
          return false;
        }

        const user_id = this.patientData._id;
        await this.useJwt().mainService.patients.update(user_id, formData);
        await this.fetchPatientDetails();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'EditIcon',
            variant: 'success',
            text: `Patient updated successfully.`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
  }
}
</script>
